// KubikonCAD — главный компонент 3D-редактора (клон Fusion 360).
//
// Структура (повторяет Fusion 360):
//   ┌─────────────────────────────────────────────┐
//   │            TopBar (Sketch | Create | ...)    │ ← главные категории операций
//   ├──────────┬──────────────────────────┬────────┤
//   │ Browser  │                          │ Props  │
//   │  (дерево │     Viewport (3D)        │  (опц. │
//   │   тел)   │                          │ только │
//   │          │                          │ когда  │
//   │          │                          │ актив- │
//   │          │                          │ на оп.)│
//   ├──────────┴──────────────────────────┴────────┤
//   │              Timeline (хронология)            │
//   └─────────────────────────────────────────────┘

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import useCadStore from './store/useCadStore';
import { getGeometryClient } from './geometryClient';
import { serializeDocument, loadDocumentFromJson } from './documentIO';
import { openFromCloud } from './cloudIO';
import { rebuildAllBodies } from './rebuildPipeline';
import * as autosave from './autosaveDb';
import { isMeaningfulDocument } from './autosaveDb';
import { installGlobalCrashHandlers, reportManual } from './crashReporter';
import RU from './i18n/ru';

import Viewport from './view3d/Viewport';
import SketchOverlayUI from './sketch/SketchOverlayUI';
import SketchCameraToolbar from './view3d/SketchCameraToolbar';
import CameraToolOverlay from './view3d/CameraToolOverlay';
import GizmoDragInput from './view3d/GizmoDragInput';
import FilletDragInput from './sketch/FilletDragInput';
import CloudSaveModal from './CloudSaveModal';
import { ViewCubeOverlay } from './view3d/ViewCube';
import TopBar from './ui/TopBar';
import Browser, { BrowserContextMenu } from './ui/Browser';
import MarkingMenu from './ui/MarkingMenu';
import Timeline from './ui/Timeline';
import Properties from './ui/Properties';
import LessonPanel from './lesson/LessonPanel';
import MyProjectsModal from './ui/MyProjectsModal';
import ParametersModal from './ui/ParametersModal';
import MeasureModal from './ui/MeasureModal';
import MeasurePanel from './ui/MeasurePanel';
import Toasts, { toast } from './ui/Toasts';
import { BUILD_TAG } from './CadErrorBoundary';

import cl from './KubikonCAD.module.css';



/* Отметка времени автосейва, восстановление которого человек уже отклонил.
   Пока документ не изменился, повторно не спрашиваем. */
const DECLINED_RESTORE_KEY = 'kubikon_cad_declined_restore';


const KubikonCAD = ({ projectId, lessonId }) => {
    const navigate = useNavigate();
    const kernelStatus = useCadStore((s) => s.kernelStatus);
    const kernelError = useCadStore((s) => s.kernelError);
    const browserVisible = useCadStore((s) => s.ui.browserVisible);
    const timelineVisible = useCadStore((s) => s.ui.timelineVisible);
    const propertiesVisible = useCadStore((s) => s.ui.propertiesVisible);
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const selection = useCadStore((s) => s.selection);
    const mode = useCadStore((s) => s.mode);
    const docFeatures = useCadStore((s) => s.doc.features);
    const docSketches = useCadStore((s) => s.doc.sketches);
    const isModified = useCadStore((s) => s.isModified);
    const [autosaveState, setAutosaveState] = useState('idle'); // 'idle'|'saving'|'saved'
    // Найденный автосейв, по которому ждём решения (null — предложения нет).
    const [restoreOffer, setRestoreOffer] = useState(null);
    // Панель урока: открыта, если редактор запущен по ссылке с уроком.
    // Ребёнок может её закрыть (мешает смотреть деталь) и открыть снова
    // кнопкой в шапке — сам урок при этом никуда не девается.
    const [lessonOpen, setLessonOpen] = useState(!!lessonId);
    useEffect(() => { setLessonOpen(!!lessonId); }, [lessonId]);

    /*
     * ОЧИСТКА СЦЕНЫ ПРИ ПЕРЕХОДЕ НА ДРУГОЙ УРОК.
     *
     * «Следующий урок» ведёт на тот же маршрут /main/cad-editor/new, меняется
     * только ?lesson=. React такой переход НЕ считает сменой страницы: компонент
     * не размонтируется, и деталь предыдущего урока остаётся в сцене. Ребёнку
     * приходилось удалять её руками, а проверка следующего задания видела два
     * тела вместо одного.
     *
     * Пересоздавать редактор через key нельзя: это перезапустит WASM-ядро OCCT
     * (несколько секунд загрузки). Поэтому чистим только документ.
     *
     * Ждём готовности ядра: newDocument() до его инициализации оставит сцену
     * в несогласованном состоянии. Первый урок не чистим — там и так пусто,
     * а лишний сброс убил бы восстановление из автосейва.
     */
    /*
     * ПОЛНОЭКРАННЫЙ РЕЖИМ (Fullscreen API).
     *
     * Класс body.kubcad-fullscreen убирает только шапку САЙТА. Панель браузера
     * (вкладки, адресная строка) остаётся и съедает высоту — для CAD это
     * заметно. Убрать её может лишь Fullscreen API.
     *
     * Автоматически включать нельзя: браузер разрешает requestFullscreen только
     * в ответ на жест пользователя, иначе бросает NotAllowedError. Поэтому —
     * кнопка. Состояние читаем из события fullscreenchange, а не из своего
     * флага: выйти можно ещё и через Esc/F11, мимо нашей кнопки.
     */
    const [isFullscreen, setIsFullscreen] = useState(
        () => typeof document !== 'undefined' && !!document.fullscreenElement
    );
    useEffect(() => {
        const onChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);
    const toggleFullscreen = React.useCallback(() => {
        if (document.fullscreenElement) {
            document.exitFullscreen?.().catch(() => {});
        } else {
            // Растягиваем документ целиком, а не контейнер редактора: иначе
            // модалки и выпадающие меню, живущие в body, окажутся вне экрана.
            document.documentElement.requestFullscreen?.().catch((e) => {
                console.warn('[fullscreen] отказано:', e?.message || e);
            });
        }
    }, []);

    const prevLessonRef = useRef(lessonId);
    useEffect(() => {
        const prev = prevLessonRef.current;
        prevLessonRef.current = lessonId;
        if (kernelStatus !== 'ready') return;
        if (!lessonId || !prev || prev === lessonId) return;
        useCadStore.getState().newDocument();
        // Автосейв тоже сбрасываем: иначе при следующем заходе редактор
        // предложит восстановить деталь из уже пройденного урока.
        autosave.clear().catch(() => {});
    }, [lessonId, kernelStatus]);
    // Properties-панель автоматически появляется когда активна операция
    // или есть выделение
    const showProperties = propertiesVisible || !!pendingFeature
        || (selection.kind && selection.ids.length > 0);

    const clientRef = useRef(null);

    // Инициализация клиента геометрии (загрузка WASM-ядра)
    useEffect(() => {
        const client = getGeometryClient();
        clientRef.current = client;
        if (!client.ready) {
            client.init().catch((e) => {
                console.error('[KubikonCAD] kernel init failed:', e);
            });
        }
        // НЕ destroy — singleton живёт всю сессию
    }, []);

    // Глобальные обработчики крашей (Месяц 12 плана — телеметрия).
    // Шлём JS-ошибки и unhandled rejections в storys для бета-тестинга.
    useEffect(() => {
        const uninstall = installGlobalCrashHandlers();
        return uninstall;
    }, []);

    // Состояние модалки «Сообщить о проблеме»
    const [reportModalOpen, setReportModalOpen] = useState(false);

    // Временно убираем скролл у contentArea (как в SchemaEditor)
    useEffect(() => {
        const contentArea = document.querySelector('[class*="contentArea"]');
        if (!contentArea) return;
        const prev = contentArea.style.overflow;
        contentArea.style.overflow = 'hidden';
        return () => { contentArea.style.overflow = prev || ''; };
    }, []);

    // Хоткеи редактора:
    //   Ctrl+B          — создать тестовый куб (отладочный)
    //   Ctrl+Z          — Undo
    //   Ctrl+Y / Shift+Z — Redo
    //
    // Если пользователь печатает в input/textarea — глотаем горячие клавиши
    // (стандартное браузерное поведение оставляем).
    useEffect(() => {
        const isInputTarget = (t) => {
            if (!t) return false;
            const tag = (t.tagName || '').toLowerCase();
            return tag === 'input' || tag === 'textarea' || t.isContentEditable;
        };

        const handler = async (e) => {
            // Ctrl+B — test cube
            if (e.ctrlKey && (e.key === 'b' || e.key === 'и')) {
                e.preventDefault();
                if (clientRef.current?.ready) {
                    clientRef.current.createTestCube();
                }
                return;
            }

            // Undo/Redo
            const ctrl = e.ctrlKey || e.metaKey;
            if (!ctrl) return;
            if (isInputTarget(e.target)) return;

            // Ctrl+Z (без Shift) → Undo
            if ((e.key === 'z' || e.key === 'я' || e.key === 'Z' || e.key === 'Я') && !e.shiftKey) {
                e.preventDefault();
                const ok = useCadStore.getState().undo();
                if (ok) {
                    try { await rebuildAllBodies(); }
                    catch (err) { console.error('[undo] rebuild failed:', err); }
                }
                return;
            }
            // Ctrl+Y или Ctrl+Shift+Z → Redo
            if (e.key === 'y' || e.key === 'н' ||
                ((e.key === 'z' || e.key === 'я' || e.key === 'Z' || e.key === 'Я') && e.shiftKey)) {
                e.preventDefault();
                const ok = useCadStore.getState().redo();
                if (ok) {
                    try { await rebuildAllBodies(); }
                    catch (err) { console.error('[redo] rebuild failed:', err); }
                }
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    // === Автосохранение в IndexedDB каждые 30 секунд ===
    // Сохраняем только когда действительно есть содержательные изменения
    // (хотя бы один sketch/feature) и пользователь что-то менял (isModified).
    // Дебаунс по taimeout: каждое изменение перезапускает 30-секундный таймер.
    useEffect(() => {
        if (!isModified) return;
        if (!docFeatures.length && !docSketches.length) return;
        const t = setTimeout(async () => {
            try {
                setAutosaveState('saving');
                const data = serializeDocument();
                if (isMeaningfulDocument(data)) {
                    await autosave.put(data);
                    setAutosaveState('saved');
                    setTimeout(() => setAutosaveState('idle'), 2000);
                }
            } catch (e) {
                console.warn('[autosave] failed:', e);
                setAutosaveState('idle');
            }
        }, 5 * 1000);
        return () => clearTimeout(t);
    }, [isModified, docFeatures, docSketches]);

    /*
     * Аварийное сохранение: при уходе со страницы и при потере видимости.
     *
     * Дебаунс-таймер выше ПЕРЕЗАПУСКАЕТСЯ на каждое изменение. Пока человек
     * активно строит, пауза не наступает — и работа не попадает в автосохранение
     * вообще. Если в этот момент вкладка закрывается или редактор падает,
     * теряется всё сделанное (у МИНа так пропал вырез в теле).
     *
     * Здесь пишем СИНХРОННО по событию: pagehide/visibilitychange — последний
     * момент, когда браузер ещё даёт выполнить код. Плюс интервал ниже как
     * страховка на случай, когда пауз в работе нет вовсе.
     */
    useEffect(() => {
        const saveNow = () => {
            try {
                const st = useCadStore.getState();
                if (!st.isModified) return;
                const data = serializeDocument();
                if (isMeaningfulDocument(data)) autosave.put(data);
            } catch (e) { /* на выгрузке страницы ошибки уже некому показать */ }
        };
        const onHide = () => { if (document.visibilityState === 'hidden') saveNow(); };
        window.addEventListener('pagehide', saveNow);
        document.addEventListener('visibilitychange', onHide);
        // Страховка: раз в 30 сек пишем независимо от пауз в работе.
        const iv = setInterval(saveNow, 30 * 1000);
        return () => {
            window.removeEventListener('pagehide', saveNow);
            document.removeEventListener('visibilitychange', onHide);
            clearInterval(iv);
        };
    }, []);

    // === Глобальный Escape — отменяет активный инструмент / выделение ===
    // Приоритет (что отменяется первым по Esc):
    //   1) pendingFeature (если активна операция Extrude/Fillet/Combine/...)
    //   2) selection (если выбрана грань/ребро/тело)
    //   3) select_plane mode (если идёт выбор плоскости для эскиза)
    //   4) sketch mode — НЕ ловим (там свой Escape в SketchOverlayUI)
    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            // Не реагируем если фокус на input/textarea/contenteditable —
            // там Esc нужен для отмены ввода, не отмены инструмента.
            const target = e.target;
            const tag = target?.tagName?.toLowerCase();
            if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return;

            const store = useCadStore.getState();
            // Если идёт fillet/chamfer preview — откатываем worker-backup
            const pf = store.pendingFeature;
            if (pf && (pf.kind === 'fillet' || pf.kind === 'chamfer')) {
                const refs = pf.edgeRefs || [];
                if (refs.length > 0) {
                    const [bodyId] = refs[0].split('/');
                    try {
                        getGeometryClient().cancelFilletPreview(bodyId);
                    } catch (_) {}
                }
            }
            // 1. Активный инструмент → отменяем
            if (pf) {
                store.setPendingFeature(null);
                store.setSelection(null, []);
                e.preventDefault();
                return;
            }
            // 2. Режим выбора плоскости → отменяем
            if (store.mode === 'select_plane') {
                store.cancelSelectPlane();
                e.preventDefault();
                return;
            }
            // 3. Просто очистка выделения (не в sketch — там свой handler)
            if (store.mode !== 'sketch' && store.selection.kind) {
                store.setSelection(null, []);
                e.preventDefault();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);


    // === Delete / Backspace — удаляет выделенное тело целиком ===
    // Срабатывает только когда выбрано именно ТЕЛО (selection.kind === 'body'),
    // а не грань/ребро (у них своя семантика) и не идёт активная операция/эскиз.
    // Логика повторяет пункт «Удалить» из контекстного меню тела:
    //   worker.deleteBody + store.removeBody. Есть Undo, поэтому без confirm.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== 'Delete' && e.key !== 'Backspace') return;
            const target = e.target;
            const tag = target?.tagName?.toLowerCase();
            if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return;

            const store = useCadStore.getState();
            // Не мешаем активному инструменту / режиму эскиза.
            if (store.pendingFeature) return;
            if (store.mode !== 'view3d') return;

            const sel = store.selection;
            if (sel.kind !== 'body' || !sel.ids.length) return;

            e.preventDefault();
            const client = getGeometryClient();
            for (const bodyId of sel.ids) {
                if (!store.bodyMeshes.has(bodyId)) continue;
                try { client.deleteBody(bodyId); }
                catch (err) { console.error('[delete] worker deleteBody failed:', bodyId, err); }
                store.removeBody(bodyId);
            }
            store.clearSelection();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);


    // === Ctrl+C / Ctrl+V — копирование тел ===
    // Ctrl+C: запоминаем bodyId выделенного тела в clipboard.
    // Ctrl+V: дублируем тело в worker (новый bodyId, та же геометрия) и сразу
    // запускаем Move-инструмент с фиксированным initialCenter, чтобы юзер
    // мог растянуть стрелками. Иначе копия лежит ровно поверх оригинала
    // и невидима.
    const clipboardRef = useRef(null); // { bodyId } | null
    useEffect(() => {
        const onKey = async (e) => {
            // Не реагируем в input/textarea/contenteditable
            const target = e.target;
            const tag = target?.tagName?.toLowerCase();
            if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return;
            if (!(e.ctrlKey || e.metaKey)) return;

            const store = useCadStore.getState();

            // Ctrl+C
            if (e.key === 'c' || e.key === 'C' || e.key === 'с' || e.key === 'С') {
                const sel = store.selection;
                if (sel.kind === 'body' && sel.ids.length > 0) {
                    clipboardRef.current = { bodyId: sel.ids[0] };
                    e.preventDefault();
                }
                return;
            }

            // Ctrl+V
            if (e.key === 'v' || e.key === 'V' || e.key === 'м' || e.key === 'М') {
                if (!clipboardRef.current) return;
                const { bodyId: srcId } = clipboardRef.current;
                // Проверяем что тело ещё существует
                if (!store.bodyMeshes.has(srcId)) {
                    clipboardRef.current = null;
                    return;
                }
                e.preventDefault();
                try {
                    const result = await getGeometryClient().duplicateBody(srcId);
                    const newId = result?.bodyId;
                    if (!newId) return;
                    // Считаем initialCenter новой копии (она = src по геометрии)
                    const mesh = useCadStore.getState().bodyMeshes.get(newId);
                    let initialCenter = [0, 0, 0];
                    if (mesh && mesh.positions && mesh.positions.length >= 3) {
                        const p = mesh.positions;
                        let minX = Infinity, minY = Infinity, minZ = Infinity;
                        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
                        for (let i = 0; i < p.length; i += 3) {
                            if (p[i] < minX) minX = p[i]; if (p[i] > maxX) maxX = p[i];
                            if (p[i+1] < minY) minY = p[i+1]; if (p[i+1] > maxY) maxY = p[i+1];
                            if (p[i+2] < minZ) minZ = p[i+2]; if (p[i+2] > maxZ) maxZ = p[i+2];
                        }
                        initialCenter = [(minX+maxX)/2, (minY+maxY)/2, (minZ+maxZ)/2];
                    }
                    // Выделяем копию + сразу запускаем Move-инструмент
                    useCadStore.getState().setSelection('body', [newId]);
                    useCadStore.getState().setPendingFeature({
                        kind: 'moveCopy',
                        bodyId: newId,
                        initialCenter,
                        dx: 0, dy: 0, dz: 0,
                        rx: 0, ry: 0, rz: 0,
                        copy: false,
                    });
                } catch (err) {
                    console.error('[Ctrl+V] duplicate failed:', err);
                    alert('Ошибка копирования: ' + (err?.message || err));
                }
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);


    // === При старте — открыть проект из облака если задан projectId ===
    // Маршрут /main/cad-editor/:id передаёт projectId через prop.
    const projectLoadedRef = useRef(false);
    useEffect(() => {
        if (kernelStatus !== 'ready') return;
        if (!projectId) return;
        if (projectLoadedRef.current) return;
        projectLoadedRef.current = true;
        (async () => {
            try {
                await openFromCloud(parseInt(projectId, 10));
            } catch (e) {
                console.error('[KubikonCAD] open cloud project failed:', e);
                alert('Не удалось открыть проект: ' + (e?.message || e));
            }
        })();
    }, [kernelStatus, projectId]);


    // === При старте без projectId — восстановить из IndexedDB если есть ===
    // Если открываем существующий проект (есть projectId) — НЕ предлагаем
    // restore из autosave, иначе перетрёт облачный.
    const restoreCheckedRef = useRef(false);
    useEffect(() => {
        if (kernelStatus !== 'ready') return;
        if (projectId) return;          // открываем облачный — autosave игнорируем
        if (restoreCheckedRef.current) return;
        restoreCheckedRef.current = true;

        (async () => {
            try {
                const saved = await autosave.get();
                if (!saved || !isMeaningfulDocument(saved)) return;

                /*
                 * 🔴 УРОК ВСЕГДА НАЧИНАЕТСЯ С ЧИСТОГО ЛИСТА.
                 *
                 * В курсе каждый урок строит свою деталь, и предлагать тут
                 * восстановление бессмысленно: сохранённое — это работа из
                 * ПРОШЛОГО урока, которую всё равно придётся удалить.
                 * Раньше диалог выскакивал при каждом входе в урок.
                 */
                if (lessonId) { await autosave.clear(); return; }

                /*
                 * Отказ ЗАПОМИНАЕМ. Прежде отказ чистил автосейв, но тот
                 * немедленно писался заново (сохранение идёт постоянно), и
                 * при следующем открытии предложение возвращалось —
                 * бесконечно, на один и тот же документ.
                 *
                 * Теперь помним отметку времени отклонённого документа: пока
                 * он не изменился, второй раз не спрашиваем.
                 */
                const declined = localStorage.getItem(DECLINED_RESTORE_KEY);
                if (declined && declined === String(saved.savedAt || '')) return;

                setRestoreOffer(saved);
            } catch (e) {
                console.warn('[autosave] restore check failed:', e);
            }
        })();
    }, [kernelStatus, projectId, lessonId]);

    return (
        <div className={cl.app}>
            {/* Верхняя строка: лого + кнопка назад + статус */}
            <div className={cl.header}>
                <button
                    className={cl.backBtn}
                    onClick={() => navigate('/main/cad-editor')}
                    title={RU.common.backToCourses}
                >
                    ←
                </button>
                <div className={cl.brand}>
                    <span className={cl.brandIcon}>🧊</span>
                    <span className={cl.brandTitle}>{RU.appTitle}</span>
                    <span className={cl.brandSub}>{RU.appSubtitle}</span>
                </div>
                <UndoRedoButtons/>
                <button
                    className={cl.reportBtn}
                    onClick={toggleFullscreen}
                    title={isFullscreen
                        ? 'Выйти из полноэкранного режима (F11 или Esc)'
                        : 'Во весь экран — панель браузера съедает высоту (F11)'}
                    type="button"
                >
                    {isFullscreen ? '🗗 Свернуть' : '🗖 Во весь экран'}
                </button>
                <button
                    className={cl.reportBtn}
                    onClick={() => setReportModalOpen(true)}
                    title="Сообщить о проблеме (баг, неудобство, идея)"
                >
                    🐞 Сообщить
                </button>
                {/* Вернуть панель урока, если её закрыли. Показываем только
                    когда редактор открыт из урока — иначе кнопке тут не место. */}
                {lessonId && !lessonOpen && (
                    <button
                        className={cl.reportBtn}
                        onClick={() => setLessonOpen(true)}
                        title="Показать задание урока"
                        type="button"
                    >
                        Задание
                    </button>
                )}
                <AutosaveIndicator state={autosaveState}/>
                {/* Метка сборки — чтобы видеть, какой код реально выполняется:
                    браузер может держать старый бандл в памяти вкладки. */}
                <span style={{
                    fontSize: 11, letterSpacing: '.04em', opacity: .55,
                    marginRight: 10, whiteSpace: 'nowrap',
                }} title="Версия сборки редактора">{BUILD_TAG}</span>
                <KernelStatusIndicator status={kernelStatus} error={kernelError}/>
            </div>

            {/* Главная панель команд — как у Fusion */}
            <TopBar onCreateTestCube={() => clientRef.current?.createTestCube()}/>

            {/* Тело редактора */}
            <div className={cl.body}>
                {browserVisible && (
                    <div className={cl.browserPane}>
                        <Browser/>
                    </div>
                )}

                {/*
                 * ⚠️ КАЖДЫЙ сосед канваса — в СВОЁЙ постоянной обёртке.
                 *
                 * Здесь рядом с <Viewport/> (живой WebGL-канвас) стоят семь
                 * узлов, часть которых появляется и исчезает по режиму:
                 * оверлеи ядра, оверлей эскиза, тулбар и pan-оверлей
                 * sketch-режима. При переходе «выбрал плоскость → взял
                 * инструмент» менялись сразу несколько — React пересчитывал
                 * позиции вставки ВОКРУГ канваса и падал с
                 * «insertBefore: узел не является дочерним», а следом
                 * терялся WebGL-контекст.
                 *
                 * Обёртки с display:contents держат число и порядок детей
                 * неизменными, не создавая своих боксов: вёрстка и клики
                 * работают ровно как раньше.
                 */}
                <div className={cl.viewportPane}>
                    {/* СЛОЙ 1 — только канвас. Ни одного меняющегося соседа:
                        перестройки оверлеев физически не могут задеть его
                        поддерево (см. комментарий в KubikonCAD.module.css). */}
                    <div className={cl.canvasLayer}>
                        <Viewport/>
                    </div>

                    {/* СЛОЙ 2 — все DOM-оверлеи. Появляются и исчезают сколько
                        угодно: канвас в другом слое и об этом не знает. */}
                    <div className={cl.overlayLayer}>
                        {kernelStatus === 'loading' && <KernelLoadingOverlay/>}
                        {kernelStatus === 'error' && <KernelErrorOverlay error={kernelError}/>}
                        {(mode === 'sketch' || mode === 'select_plane') && <SketchOverlayUI/>}
                        {/* Fusion-style toolbar навигации (только в sketch-mode) */}
                        {mode === 'sketch' && <SketchCameraToolbar/>}
                        {/* Pan-overlay поверх сцены (когда активен pan-tool) */}
                        {mode === 'sketch' && <CameraToolOverlay/>}
                        {/* Live-инпут смещения при drag за TransformGizmo */}
                        <GizmoDragInput/>
                        {/* Live-инпут радиуса при drag за стрелку fillet/chamfer */}
                        <FilletDragInput/>
                        {/* ViewCube — DOM-overlay (CSS-3D куб с гранями) */}
                        <ViewCubeOverlay/>
                    </div>
                </div>

                {showProperties && (
                    <div className={cl.propertiesPane}>
                        <Properties/>
                    </div>
                )}

                {/* Панель урока — крайняя справа. Она СНАРУЖИ .viewportPane,
                    то есть не сосед канваса: её появление и исчезновение не
                    может задеть порядок вставки узлов вокруг WebGL. */}
                {lessonOpen && (
                    <LessonPanel
                        lessonId={lessonId}
                        onClose={() => setLessonOpen(false)}
                    />
                )}
            </div>

            {/* Хронология построения снизу */}
            {timelineVisible && (
                <div className={cl.timelinePane}>
                    <Timeline/>
                </div>
            )}

            {/* Контекстное меню тела (правый клик в 3D-сцене) */}
            <BodyContextMenuOverlay/>
            <ProfileContextMenuOverlay/>
            <CloudSaveModal/>
            <ParametersModalMount/>
            <MeasureModalMount/>
            <MeasurePanel/>
            {/*
              * Предложение восстановить прошлую работу.
              *
              * Не window.confirm: тот блокирует весь редактор и требует
              * ответа немедленно, хотя человек ещё не понял, что ему
              * предлагают (правило проекта — не использовать нативные
              * диалоги). Панель в углу ждёт столько, сколько нужно, и
              * не мешает осмотреться.
              */}
            {restoreOffer && (
                <div className={cl.restorePanel}>
                    <div className={cl.restoreTitle}>Найдена прошлая работа</div>
                    <div className={cl.restoreInfo}>
                        {restoreOffer.savedAt
                            ? new Date(restoreOffer.savedAt).toLocaleString('ru-RU')
                            : 'время неизвестно'}
                        {' · '}
                        {(restoreOffer.features || []).length} операций,
                        {' '}{(restoreOffer.sketches || []).length} эскизов
                    </div>
                    <div className={cl.restoreActions}>
                        <button
                            type="button"
                            className={cl.restorePrimary}
                            onClick={async () => {
                                const doc = restoreOffer;
                                setRestoreOffer(null);
                                try {
                                    await loadDocumentFromJson(doc);
                                } catch (e) {
                                    toast('Не удалось восстановить: '
                                        + (e?.message || e), 'error');
                                }
                            }}
                        >
                            Восстановить
                        </button>
                        <button
                            type="button"
                            className={cl.restoreSecondary}
                            onClick={() => {
                                // Запоминаем отказ по этому документу, чтобы
                                // предложение не возвращалось при каждом входе.
                                try {
                                    localStorage.setItem(
                                        DECLINED_RESTORE_KEY,
                                        String(restoreOffer.savedAt || ''),
                                    );
                                } catch (_) { /* приватный режим — не критично */ }
                                setRestoreOffer(null);
                            }}
                        >
                            Начать заново
                        </button>
                    </div>
                </div>
            )}

            <Toasts/>

            {/* Облачные проекты — модалка «Мои проекты» */}
            <CloudProjectsModalMount/>

            {/* «Сообщить о проблеме» — Месяц 12 плана */}
            {reportModalOpen && (
                <ReportProblemModal onClose={() => setReportModalOpen(false)}/>
            )}
        </div>
    );
};


// === Модалка «Сообщить о проблеме» ===
// Юзер пишет описание, мы шлём в /kubikon-cad/crash как category='manual'.
const ReportProblemModal = ({ onClose }) => {
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const onSubmit = async () => {
        if (!text.trim()) {
            alert('Опиши что произошло — даже коротко поможет');
            return;
        }
        setSending(true);
        try {
            await reportManual(text.trim());
            setSent(true);
            setTimeout(onClose, 1400);
        } catch (e) {
            alert('Не удалось отправить: ' + (e.message || e));
            setSending(false);
        }
    };

    return (
        <div className={cl.reportBackdrop} onClick={onClose}>
            <div className={cl.reportModal} onClick={(e) => e.stopPropagation()}>
                <div className={cl.reportHeader}>
                    <span>🐞 Сообщить о проблеме</span>
                    <button className={cl.reportClose} onClick={onClose}>✕</button>
                </div>
                {sent ? (
                    <div className={cl.reportSent}>
                        ✅ Спасибо! Мы получили твой репорт.
                    </div>
                ) : (
                    <>
                        <p className={cl.reportHint}>
                            Опиши коротко что произошло. Мы увидим автоматически:
                            твою последнюю ошибку (если была), версию редактора и
                            сколько фич/тел в документе. Не нужно вставлять стек.
                        </p>
                        <textarea
                            className={cl.reportTextarea}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder="Например: при нажатии OK в Выдавливании ничего не происходит, в консоли красная ошибка про vertices…"
                            autoFocus
                            rows={6}
                            maxLength={2000}
                        />
                        <div className={cl.reportActions}>
                            <button
                                className={cl.reportSecondary}
                                onClick={onClose}
                                disabled={sending}
                            >
                                Отмена
                            </button>
                            <button
                                className={cl.reportPrimary}
                                onClick={onSubmit}
                                disabled={sending || !text.trim()}
                            >
                                {sending ? 'Отправка…' : 'Отправить'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};


// Монтирует модалку «Мои проекты» по флагу из store
const CloudProjectsModalMount = () => {
    const open = useCadStore((s) => s.cloudModalOpen);
    const setOpen = useCadStore((s) => s.setCloudModalOpen);
    if (!open) return null;
    return <MyProjectsModal onClose={() => setOpen(false)}/>;
};


const ParametersModalMount = () => {
    const open = useCadStore((s) => s.parametersModalOpen);
    const setOpen = useCadStore((s) => s.setParametersModalOpen);
    if (!open) return null;
    return <ParametersModal onClose={() => setOpen(false)}/>;
};


const MeasureModalMount = () => {
    const open = useCadStore((s) => s.measureModalOpen);
    const setOpen = useCadStore((s) => s.setMeasureModalOpen);
    if (!open) return null;
    return <MeasureModal onClose={() => setOpen(false)}/>;
};


// === Контекстное меню тела при правом клике в 3D-сцене ===
// Показывается DOM-overlay'ем поверх Canvas — координаты берём из store.
const BodyContextMenuOverlay = () => {
    const ctx = useCadStore((s) => s.bodyContextMenu);
    const close = useCadStore((s) => s.closeBodyContextMenu);
    const visibility = useCadStore((s) => s.bodyVisibility);
    const toggleBodyVisible = useCadStore((s) => s.toggleBodyVisible);
    const removeBody = useCadStore((s) => s.removeBody);

    if (!ctx) return null;
    const { bodyId, x, y } = ctx;
    const visible = visibility.has(bodyId) ? visibility.get(bodyId) : true;

    const onDelete = () => {
        close();
        if (!window.confirm(`Удалить тело ${bodyId}?`)) return;
        getGeometryClient().deleteBody(bodyId);
        removeBody(bodyId);
    };

    /*
     * Радиальное меню вместо списка — как в Fusion.
     *
     * Раскладка постоянна: вверху «Повторить», слева «Отменить», справа
     * «Вернуть». Именно неизменность мест делает жест запоминаемым: рука
     * тянется в сторону, не глядя. Остальные секторы — ходовые операции
     * над телом.
     */
    const items = [
        { id: 'repeat',   label: 'Повторить' },
        { id: 'fillet',   label: 'Скругление' },
        { id: 'redo',     label: 'Вернуть' },
        { id: 'shell',    label: 'Оболочка' },
        { id: 'delete',   label: 'Удалить' },
        { id: 'move',     label: 'Переместить' },
        { id: 'undo',     label: 'Отменить' },
        { id: 'hide',     label: visible ? 'Скрыть' : 'Показать' },
    ];

    const pick = (id) => {
        const st = useCadStore.getState();
        if (id === 'hide')   { toggleBodyVisible(bodyId); return; }
        if (id === 'delete') { onDelete(); return; }
        if (id === 'undo')   { st.undo?.(); return; }
        if (id === 'redo')   { st.redo?.(); return; }
        if (id === 'repeat') { st.repeatLastCommand?.(); return; }
        // Операции над телом: выделяем его и открываем панель.
        st.setSelection('body', [bodyId]);
        if (id === 'fillet') st.setPendingFeature({ kind: 'fillet', bodyId, radius: 2, edgeRefs: [] });
        if (id === 'shell')  st.setPendingFeature({ kind: 'shell', bodyId, thickness: 2, faceRefs: [] });
        if (id === 'move')   st.setPendingFeature({ kind: 'moveCopy', bodyIds: [bodyId], dx: 0, dy: 0, dz: 0 });
    };

    return (
        <MarkingMenu
            at={{ x, y }}
            items={items}
            onPick={(id) => { close(); pick(id); }}
            onClose={close}
        />
    );
};


// Контекстное меню профиля эскиза (клик по замкнутой области в 3D).
// Действия запускают операцию с уже выбранным профилем (Выдавить/Вращение/…).
const ProfileContextMenuOverlay = () => {
    const ctx = useCadStore((s) => s.profileContextMenu);
    const close = useCadStore((s) => s.closeProfileContextMenu);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const setSelection = useCadStore((s) => s.setSelection);

    if (!ctx) return null;
    const { profileRefs, x, y } = ctx;
    const refs = profileRefs && profileRefs.length ? profileRefs : [];
    if (!refs.length) return null;
    const n = refs.length;
    const suffix = n > 1 ? ` (${n})` : '';

    const start = (feature) => {
        setSelection('profile', refs);
        /* faceProfile от прежнего выбора грани обнуляем явно: иначе он
           переживёт старт операции и Properties (он смотрит faceProfile
           первым) выдавит грань целиком вместо выбранного профиля эскиза. */
        setPendingFeature({ ...feature, faceProfile: null, faceRef: null });
        close();
    };

    return (
        <BrowserContextMenu
            x={x} y={y}
            onClose={close}
            items={[
                { label: 'Выдавить' + suffix, onClick: () => start({
                    kind: 'extrude', profileRefs: refs,
                    distance: 20, direction: 'one', operation: 'new' }) },
                { label: 'Вращение' + suffix, onClick: () => start({
                    kind: 'revolve', profileRefs: refs,
                    axis: 'sketch_Y', angle: 360, operation: 'new' }) },
                // Sweep берёт первый профиль как сечение (для нескольких не применим).
                { label: 'Сдвиг по траектории', onClick: () => start({
                    kind: 'sweep', profileRef: refs[0], pathRef: null, operation: 'new' }) },
                { label: 'Лофт' + suffix, onClick: () => start({
                    kind: 'loft', profileRefs: refs, operation: 'new' }) },
            ]}
        />
    );
};


// Кнопки Undo/Redo в header. Показывают историю past/future.
// Disabled если стек пустой. Хоткеи Ctrl+Z / Ctrl+Y / Ctrl+Shift+Z — отдельно.
const UndoRedoButtons = () => {
    const past   = useCadStore((s) => s.historyPast);
    const future = useCadStore((s) => s.historyFuture);

    const onUndo = async () => {
        const ok = useCadStore.getState().undo();
        if (ok) {
            try { await rebuildAllBodies(); }
            catch (e) { console.error('[undo] rebuild failed:', e); }
        }
    };
    const onRedo = async () => {
        const ok = useCadStore.getState().redo();
        if (ok) {
            try { await rebuildAllBodies(); }
            catch (e) { console.error('[redo] rebuild failed:', e); }
        }
    };

    return (
        <div className={cl.undoRedo}>
            <button
                className={cl.undoBtn}
                onClick={onUndo}
                disabled={past.length === 0}
                title={`Отменить (Ctrl+Z) · в стеке: ${past.length}`}
            >
                ↶
            </button>
            <button
                className={cl.undoBtn}
                onClick={onRedo}
                disabled={future.length === 0}
                title={`Повторить (Ctrl+Y) · в стеке: ${future.length}`}
            >
                ↷
            </button>
        </div>
    );
};


// Маленький индикатор автосохранения. 'saving' (тёплый янтарь),
// 'saved' (зелёный галочки), 'idle' (скрыт).
const AutosaveIndicator = ({ state }) => {
    if (state === 'idle') return null;
    const dot = state === 'saving' ? '#dca838' : '#0c8';
    const text = state === 'saving' ? 'Сохранение…' : 'Сохранено';
    return (
        <div className={cl.kernelStatus} title={text} style={{ marginRight: 8 }}>
            <span className={cl.kernelDot} style={{ background: dot }}/>
            <span>{text}</span>
        </div>
    );
};


const KernelStatusIndicator = ({ status, error }) => {
    let dot, text;
    if (status === 'ready')   { dot = '#0c8';     text = 'Ядро готово'; }
    else if (status === 'loading') { dot = '#dca838'; text = 'Загрузка ядра…'; }
    else if (status === 'error')   { dot = '#cc3344'; text = error || 'Ошибка'; }
    else                           { dot = '#888';    text = 'Ожидание'; }
    return (
        <div className={cl.kernelStatus} title={text}>
            <span className={cl.kernelDot} style={{ background: dot }}/>
            <span>{text}</span>
        </div>
    );
};


const KernelLoadingOverlay = () => (
    <div className={cl.overlay}>
        <div className={cl.overlayInner}>
            <div className={cl.spinner}/>
            <div className={cl.overlayTitle}>{RU.common.loading}</div>
            <div className={cl.overlayHint}>{RU.common.loadingHint}</div>
        </div>
    </div>
);


const KernelErrorOverlay = ({ error }) => (
    <div className={cl.overlay}>
        <div className={`${cl.overlayInner} ${cl.overlayError}`}>
            <div className={cl.overlayTitle}>⚠ {RU.common.loadingError}</div>
            {error && <div className={cl.overlayErrorMsg}>{error}</div>}
        </div>
    </div>
);

export default KubikonCAD;
